import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Loader from "Common/Loader";
import DataGrid, {
    Pager,
    Paging,
    SearchPanel,
    Column,
    Summary,
    TotalItem,
    Toolbar,
    Item,
    Scrolling,
} from "devextreme-react/cjs/data-grid";
import { Card, Modal, Pagination } from "react-bootstrap";
import moment from 'moment';
import CustomStore from 'devextreme/data/custom_store';
import DateRangeBox from 'devextreme-react/date-range-box';
import TextBox, { type TextBoxTypes } from 'devextreme-react/text-box';
import { Button } from 'devextreme-react/button';
import { FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import type { ValueChangedEvent } from 'devextreme/ui/date_range_box';
import { GetRevenueCollection, GetRevenueReport } from '../services/revenueReport.service';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import CollectionDetailsModal from './CollectionDetailsModal';
import notistack from 'modules/core/notistack';
import ExcelImage from '../../../../../assets/images/excel.jpg';

const RevenueReportDataList = () => {
    const [searchText, setSearchText] = useState('');
    const [hasData, setHasData] = useState(false);
    const now = new Date();
    const msInDay = 1000 * 60 * 60 * 24;
    const initialValue: [Date, Date] = [
        new Date(now.getTime() - msInDay * 3),
        new Date(now.getTime() + msInDay * 3),
    ];
    const [selectedRange, setSelectedRange] = useState<[Date, Date] | null>(initialValue);
    const gridRef = useRef(null);
    const onCurrentValueChange = useCallback((e: ValueChangedEvent) => {
        const value = e.value as [Date, Date] | undefined;
        if (value && value[0] && value[1]) {
            const [startDate, endDate] = value;
            setSelectedRange([startDate, endDate]);
        } else {
            setSelectedRange(null);
        }
    }, []);
    function formatDateTime(date: Date): string {
        const pad = (n: number) => n.toString().padStart(2, '0');
        const day = pad(date.getDate());
        const month = pad(date.getMonth() + 1);
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
    }

    // const dataSource = new CustomStore({
    //     key: 'id',
    //     load: async (loadOptions) => {
    //         try {
    //             const search = JSON.parse(JSON.stringify(loadOptions));
    //             let parameterList = new URLSearchParams();
    //             for (const [key, value] of Object.entries(search)) {
    //                 parameterList.append(key, JSON.stringify(value));
    //             }
    //             if (selectedRange) {
    //                 parameterList.append("FromDate", formatDateTime(selectedRange[0]));
    //                 parameterList.append("ToDate", formatDateTime(selectedRange[1]));

    //             }

    //             if (searchText) {
    //                 parameterList.append("Search", searchText);
    //             }
    //             const response = await GetRevenueReport(parameterList);
    //             setHasData(!!(response?.data && response.data.length > 0));
    //             return {
    //                 data: response?.data || [],
    //                 totalCount: response?.data.length || 0,
    //             };

    //         } catch (error) {
    //             console.error("Data loading error:", error);
    //             return { data: [], totalCount: 0 };
    //         }
    //     }
    // });

    const dataSource = (): any | undefined => {
        return new CustomStore({
            key: "id",
            load: async (loadOptions) => {
                try {
                    const search = JSON.parse(JSON.stringify(loadOptions));
                    let parameterList = new URLSearchParams();
                    for (const [key, value] of Object.entries(search)) {
                        parameterList.append(key, JSON.stringify(value));
                    }
                    if (selectedRange) {
                        parameterList.append("FromDate", formatDateTime(selectedRange[0]));
                        parameterList.append("ToDate", formatDateTime(selectedRange[1]));

                    }
                    if (searchText) {
                        parameterList.append('Search', searchText)
                    }
                    const response = await GetRevenueReport(parameterList);
                    setHasData(!!(response?.data && response.data.length > 0));
                    return response;
                } catch (error) {
                    console.error("Data loading error:", error);
                    return { data: [], totalCount: 0 };
                }
            },
        });
    };


    const handleExportExcel = async () => {
        try {

            const loadOptions = {
                FromDate: selectedRange ? formatDateTime(selectedRange[0]) : '',
                ToDate: selectedRange ? formatDateTime(selectedRange[1]) : '',
            };

            // Create URL parameters
            const params = new URLSearchParams();
            for (const [key, value] of Object.entries(loadOptions)) {
                if (value !== undefined) {
                    params.append(key, value.toString());
                }
            }

            // Make the API call directly instead of using dataSource.load()
            const response = await GetRevenueReport(params);

            if (!response?.data || response.data.length === 0) {
                notistack.error("No data available to export");
                return;
            }

            const exportData = response.data.map(({ id, createdOn, ...rest }: any) => rest);
            const worksheet = XLSX.utils.json_to_sheet(exportData);

            // Set column widths
            worksheet['!cols'] = [
                { wch: 25 }, // Payment Type
                { wch: 15 }, // Payment Count
                { wch: 20 }  // Total Amount
            ];

            // Create workbook
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Revenue Report");

            // Export to Excel
            XLSX.writeFile(workbook, `Revenue_Report_${moment().format("YYYY-MM-DD")}.xlsx`);

        } catch (error: any) {
            console.error("Export error:", error);
            notistack.error(error.response?.data?.message || "Failed to export data");
        }
    };

    const [showModal, setShowModal] = useState(false)
    const [collectionDetails, setCollectionDetails] = useState<any[]>([]);
    const handleShow = async (paymentType: string) => {
        try {
            const parameterList: any = {};

            if (selectedRange) {
                parameterList.FromDate = formatDateTime(selectedRange[0]);
                parameterList.ToDate = formatDateTime(selectedRange[1]);
            }
            if (paymentType) {
                parameterList.paymentType = paymentType;
            }

            const response = await GetRevenueCollection(parameterList);
            if (response?.data) {
                setCollectionDetails(response.data);
                setShowModal(true);
            } else {
                setCollectionDetails([]);
            }
        } catch (error) {
            console.error(error);
            setCollectionDetails([]);
        }
    };


    return (
        <>
            <div className="page-content" style={{ maxWidth: '100vw', overflowX: 'auto' }}>
                <div className="container-fluid">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="m-0">Revenue Report</h5>
                            <div className="mt-0">
                                {hasData && (
                                    <Button
                                        icon="https://img.favpng.com/18/7/13/scalable-vector-graphics-microsoft-excel-clip-art-portable-network-graphics-png-favpng-yynmZdMbFF2A9c7tcbZa6Ewv5.jpg"
                                        hint="Export to Excel"
                                        onClick={handleExportExcel}
                                    />
                                )}
                            </div>
                        </Card.Header>

                        <Card.Body>
                            <div className="custom-toolbar w-auto d-flex flex-wrap gap-2 mb-3 ">
                                <div style={{ width: "100%", display: "flex" }} className='w-100 d-flex flex-end gap-3'>
                                    <div style={{ display: 'flex', flexDirection: 'column' }} className='mt-1'>
                                        <label>Search</label>
                                        <TextBox
                                            width={180}
                                            placeholder="Search..."
                                            value={searchText}
                                            onValueChanged={(e) => {
                                                setSearchText(e.value)
                                            }}
                                        // onKeyDown={(e: any) => {
                                        //     if (e.event.key === "Enter" || e.event.key === "Tab") {
                                        //         return 
                                        //     }
                                        // }}
                                        />
                                    </div>

                                    <div>
                                        <DateRangeBox
                                            width={300}
                                            ref={gridRef}
                                            defaultValue={['', '']}
                                            displayFormat="dd/MM/yyyy"
                                            showClearButton={true}
                                            acceptCustomValue={false}
                                            onValueChanged={onCurrentValueChange}
                                        />
                                    </div>




                                </div>

                            </div>

                            <div className='sales-inventory-grid'>
                                <DataGrid
                                    dataSource={dataSource()}
                                    keyExpr="id"
                                    showBorders={true}
                                    columnAutoWidth={true}
                                    columnHidingEnabled={false}
                                    width="100%"
                                >

                                    <Column
                                        caption="Action"
                                        width={100}
                                        alignment="center"
                                        cellRender={(cellData) => (
                                            <button
                                                className='btn btn-sm'
                                                onClick={() => {
                                                    handleShow(cellData?.data?.paymentType)
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faEye} title="View details" className="ml-2 " />
                                            </button>
                                        )}
                                    />
                                    <Paging enabled={false} />
                                    <Column dataField="paymentType" caption="Payment Type" dataType="string" width={200} alignment="center" />
                                    <Column dataField="paymentCount" caption="Payment Count" dataType="number" width={150} alignment="center" />
                                    <Column dataField="totalAmount" caption="Total Amount" dataType="number" format="#,##0.00" width={150} alignment="center" />
                                    {/* <Column dataField="createdOn" caption="Created On" dataType="date" format="dd/MM/yyyy HH:mm" width={200} alignment="center" /> */}

                                    <Summary>
                                        <TotalItem
                                        // column="totalAmount"
                                        // summaryType="sum"
                                        // valueFormat="#,##0.00"
                                        // displayFormat="Total: {0}"
                                        />
                                    </Summary>
                                </DataGrid>

                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {/*Collection Details Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Collection Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <CollectionDetailsModal data={collectionDetails} />
                </Modal.Body>
            </Modal>

        </>
    )
}

export default RevenueReportDataList


